import { ErrorType } from "../../../models/HttpError";
import { Settlement } from "../../../models/Entities/Settlements/Settlement";
import { NewProviderSettlement } from "../../../models/Entities/Settlements/NewSettlement";
import { ProviderConsumptionTotals } from "../../../models/Entities/ProviderConsumptions/ProviderConsumptions";

// stores
import * as SettlementStore from '../../../redux/middleware/SettlementMiddleware';
import * as ContractStore from '../../../redux/middleware/ContractMiddleware';
import * as ProtocolStore from '../../../redux/middleware/MedicalProtocolMiddleware';

//State
import { AppState } from "../../../redux/reducers";
import { Contract } from "../../../models/Entities/Contracts/Contract";
import { Practitioner } from "../../../models/Entities/Practitioners/Practitioner";
import { Protocol } from "../../../models/Entities/Protocols/Protocol";
import { MedicalInsurance } from "../../../models/Entities/MedicalInsurances/MedicalInsurance";
import { MedicalInsuranceAgreement } from "../../../models/Entities/MedicalInsuranceAgreements/MedicalInsuranceAgreement";
import moment from "moment";
import { ContractProtocolAssociation } from "../../../models/Entities/Contracts/ContractProtocolAssociation";


/** Proveedor de la liquidación de proveedor (spec 010): identidad que viaja en el alta tipo 4. */
export interface SettlementFormProvider {
  /** Provider.id (int). */
  id: number;
  /** Provider.person.personIdentifier (Guid). */
  personIdentifier: string;
  businessName: string;
  /** CUIT / documento, sólo para el contexto del encabezado. */
  taxId?: string;
}

interface IProps {
  isOpen: boolean;
  openPanel: () => void;
  dismissPanel: () => void;
  ContractId?: number;
  PractitionerId?: string;
  ProtocolId?: number;
  MedicalAgreementId?: number;
  MedicalInsuranceId?: number;
  /** Modo proveedor (spec 010): nombre opcional, período informativo y alta tipo 4. */
  Provider?: SettlementFormProvider;
  /** Lo que está por liquidar (barra "Por liquidar"): resumen y monedas del alta. */
  ToSettleTotals?: ProviderConsumptionTotals;
  /** Alta correcta en modo proveedor (para navegar al detalle). */
  onCreated?: (settlement: Settlement) => void;
  /** "Ver liquidación" del 409 en modo proveedor. */
  onOpenSettlement?: (settlementId: number) => void;
}
  
interface ConnectedProps {
  isAdding: boolean;
  isSuccessfullyAdded: boolean;
  failOnAdding: boolean;
  error: ErrorType | undefined;
  Contract: Contract | undefined;
  Practitioner: Practitioner | undefined;
  Protocol: Protocol | undefined;
  MedicalAgreement: MedicalInsuranceAgreement | undefined;
  MedicalInsurance: MedicalInsurance | undefined;
}

interface SettlementDispatchProps {
  AddAgreementSettlement: typeof SettlementStore.actionCreators.AddAgreementSettlement;
  AddProtocolSettlement: typeof SettlementStore.actionCreators.AddProtocolSettlement;
  AddPractitionerSettlement: typeof SettlementStore.actionCreators.AddPractitionerSettlement;
  /** Thunk con promesa (spec 010): resuelve con la liquidación o rechaza con el ErrorType (409 con data.settlementId). */
  AddProviderSettlement: (newSettlement: NewProviderSettlement) => Promise<Settlement>;
  GetContractById: typeof ContractStore.actionCreators.GetContractById;
  AssociateContractToProtocol: typeof ContractStore.actionCreators.AssociateContractToProtocol;
  GetMedicalProtocolById: typeof ProtocolStore.actionCreators.GetMedicalProtocolById;
  GetAllProtocolContractAssociations: typeof ProtocolStore.actionCreators.GetAllProtocolContractAssociations;
  GetSettlementById: typeof SettlementStore.actionCreators.GetSettlementById;
}

export type Props = IProps & ConnectedProps & SettlementDispatchProps;

export const mapStateToProps = (state: AppState, ownProps: IProps) => {

  var Contract : Contract | undefined;

  if(ownProps.PractitionerId) {
    Contract = state.Contract?.list?.find((contract: Contract) => contract.practitioners[0]?.practitionerId === ownProps.PractitionerId);
  } 

  if(ownProps.ProtocolId) {
    Contract = state.Contract?.list?.find((contract: Contract) => contract.protocol?.find((protocol: ContractProtocolAssociation) => protocol.protocolId === ownProps.ProtocolId));
  }

  const ContractProtocolAssociated : number | undefined = state.Contract?.list?.find((contract: Contract) => contract?.id === ownProps.ContractId)?.protocol[0]?.protocolId;
  const ContractPractitionerAssociated : string | undefined = state.Contract?.list?.find((contract: Contract) => contract?.id === ownProps.ContractId)?.practitioners[0]?.practitionerId;

  return {
    isAdding: state.Settlement?.isAddingNewOne,
    isSuccessfullyAdded: state.Settlement?.successAddingNewOne,
    failOnAdding: state.Settlement?.failOnAddingNewOne,
    error: state.Settlement?.error,
    Contract: ownProps.ContractId ? state.Contract?.list?.find(c => c.id === ownProps.ContractId) : Contract,
    Practitioner: ownProps.PractitionerId ? state.Practitioners?.list?.find(p => p.userId === ownProps.PractitionerId) : (ownProps.ContractId && ContractPractitionerAssociated) ? state.Practitioners?.list?.find((practitioner: Practitioner) => practitioner.userId === ContractPractitionerAssociated)  : undefined,
    Protocol: ownProps.ProtocolId ? state.MedicalProtocols?.list?.find(p => p.id === ownProps.ProtocolId) : (ownProps.ContractId && ContractProtocolAssociated) ? state.MedicalProtocols?.list?.find((protocol: Protocol) => protocol.id === ContractProtocolAssociated) : undefined,
    MedicalAgreement: ownProps.MedicalAgreementId ? state.MedicalInsuranceAgreements?.list?.find(m => m.id === Number(ownProps.MedicalAgreementId)) : undefined,
    MedicalInsurance: ownProps.MedicalInsuranceId ? state.MedicalInsurances?.list?.find((medicalInsurance: MedicalInsurance) => medicalInsurance.id === Number(ownProps.MedicalInsuranceId)) : undefined
  }
};

export const mapDispatchToProps = {
  ...SettlementStore.actionCreators,
  ...ContractStore.actionCreators,
  ...ProtocolStore.actionCreators
};


const pad = (value: number) => String(value).padStart(2, '0');

/** aaaa-mm-dd de una fecha local. */
export const toDateInput = (date: Date): string => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** Período informativo por defecto del alta de proveedor: el mes de `today` (primer y último día). */
export const currentMonthPeriod = (today: Date = new Date()): { from: string; to: string } => ({
  from: toDateInput(new Date(today.getFullYear(), today.getMonth(), 1)),
  to: toDateInput(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
});

/** aaaa-mm-dd → Date a medianoche UTC (así viaja el mismo día que se eligió). */
export const dateInputToUtc = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(Date.UTC(year, (month || 1) - 1, day || 1));
};

/** dd/mm/aaaa de hoy (ayuda del nombre por defecto de la API). */
export const todayText = (today: Date = new Date()): string => `${pad(today.getDate())}/${pad(today.getMonth() + 1)}/${today.getFullYear()}`;

export const SuggestSettlementTitle = (entity: string): string => {
  return 'Liquidación ' + entity +  ' - ' +
    moment(new Date()).format('DD/MM/YYYY HH:mm');
}